import {AoView} from "./Transports";
import {TransportTrack, TransportTrackGenerator, TransportTrackHost} from "./TransportTrackApi";
import {sortNumberAsc, sortNumberDesc} from "../lib/langExtensions";
import {createSvgElement} from "../lib/domFunctions";
import {AppConfig} from "../AppConfig";

export function createTransportTrackGenerator(player: TransportTrackHost): TransportTrackGenerator {
	return AppConfig.directPathsOnSchema
		? new DirectLineGenerator(player)
		: new SegmentLineGenerator(player)
}

class SegmentLineGenerator implements TransportTrackGenerator {
	private readonly player: TransportTrackHost;

	constructor(player: TransportTrackHost) {
		this.player = player;
	}

	get positions() {
		return this.player.positions
	}

	get positionLineY() {
		return this.player.positionLineY
	}

	get root() {
		return this.player.root
	}

	get mirrorSchema() {
		return this.player.mirrorSchema
	}

	createAoTrack(ao: AoView, from: number, to: number): TransportTrack | null {
		if (!this.positions.has(from) || !this.positions.has(to)) return null;

		let start = this.positions.get(from)!;
		const finish = this.positions.get(to)!;

		// разные линии - не рисуем
		if (start.data.line !== finish.data.line) return null

		const positions = Array.from(this.positions.values());

		const positionsOnLineY = (lineY: number) => {
			return positions.filter(p => p.lineY == lineY).sort((a, b) => a.x - b.x);
		}

		const nextLineY = (lineY: number): number => {
			return this.positionLineY.filter(y => y > lineY).sort(sortNumberAsc)[0]
		}

		const prevLineY = (lineY: number): number => {
			return this.positionLineY.filter(y => y < lineY).sort(sortNumberDesc)[0]
		}

		const addTrackSegment = (y: number, x1: number, x2: number, x1Marker: string, x2Marker: string) => {
			if (x2Marker.length == 0) x2Marker = x2 > x1 ? 'r' : 'l';
			if (x1Marker.length > 0) x1Marker = `svg-ao-track-marker-${x1Marker}`;
			if (x2Marker.length > 0) x2Marker = `svg-ao-track-marker-${x2Marker}`;

			const line = createSvgElement('path', {class: `svg-ao-track ${x1Marker} ${x2Marker}`, d: `M ${x1 + .5},${y + .5} H ${x2 - .5}`})
			this.root.insertBefore(line, this.root.firstElementChild)
			segments.push(line);
		}

		// В случае переноса линии - Y координаты будут разные. 
		// Надо тогда вычислить сегменты, и сделать трек для каждого сегмента
		const segments: SVGElement[] = [];
		const positionMarginX = 9;

		let startX = Math.floor(start.x + start.width / 2);
		let startY;
		while (start.lineY != finish.lineY) {
			const startLinePositions = positionsOnLineY(start.lineY);
			startY = Math.floor(start.y - ao.lineYOffset + ao.trackPointY);

			// если для перехода на другую линию надо ехать влево ...
			let jumpAtLeft = start.lineY > finish.lineY;
			// инвертируем, если зеркало
			if (this.mirrorSchema) {
				jumpAtLeft = !jumpAtLeft;
			}

			const nextLineYFunc = jumpAtLeft
				? (this.mirrorSchema ? nextLineY : prevLineY)
				: (this.mirrorSchema ? prevLineY : nextLineY);

			if (jumpAtLeft) {
				const segmentEnd = startLinePositions[0];
				const segmentEndX = Math.round(segmentEnd.x - positionMarginX);
				addTrackSegment(startY, startX, segmentEndX, 'sw', 'ew')

				const nextLine = nextLineYFunc(start.lineY);
				if (!nextLine) break;
				start = positionsOnLineY(nextLine).reverse()[0];
				startX = start.x + start.width + positionMarginX;
			} else {
				const segmentEnd = startLinePositions[startLinePositions.length - 1];
				const segmentEndX = Math.round(segmentEnd.x + segmentEnd.width + positionMarginX);
				addTrackSegment(startY, startX, segmentEndX, 'sw', 'ew')

				const nextLine = nextLineYFunc(start.lineY);
				if (!nextLine) break;
				start = positionsOnLineY(nextLine)[0];
				startX = start.x - positionMarginX;
			}
		}

		// добавляем последний сегмент
		startY = Math.floor(start.y - ao.lineYOffset + ao.trackPointY);
		addTrackSegment(startY, startX, Math.floor(finish.centerX), 'sw', '');

		return {
			remove() {
				segments.forEach(s => s.remove());
			}
		};
	}

}

class DirectLineGenerator implements TransportTrackGenerator {
	private readonly player: TransportTrackHost;

	constructor(player: TransportTrackHost) {
		this.player = player;
	}

	private get positions() {
		return this.player.positions
	}

	private get root() {
		return this.player.root
	}

	createAoTrack(ao: AoView, from: number, to: number): TransportTrack | null {
		if (!this.positions.has(from) || !this.positions.has(to)) return null;

		const start = this.positions.get(from)!;
		const finish = this.positions.get(to)!;

		const addTrackSegment = (x: number, y: number, x2: number, y2: number, x1Marker: string, x2Marker: string) => {
			const line = createSvgElement('path', {class: `svg-ao-track ${x1Marker} ${x2Marker}`, d: `M ${x + .5},${y + .5} L ${x2 - .5},${y2 - .5}`})
			this.root.insertBefore(line, this.root.firstElementChild)
			segments.push(line);
		}

		// В случае переноса линии - Y координаты будут разные. 
		// Надо тогда вычислить сегменты, и сделать трек для каждого сегмента
		const segments: SVGElement[] = [];

		let startX = Math.floor(start.x + start.width / 2);
		let startY = Math.floor(start.y - ao.lineYOffset + ao.trackPointY);
		let finishX = Math.floor(finish.x + finish.width / 2);
		let finishY = Math.floor(finish.y - ao.lineYOffset + ao.trackPointY);

		// добавляем последний сегмент
		console.info(`render track ${startX} ${startY} ${finishX} ${finishY}`)
		addTrackSegment(startX, startY, finishX, finishY, '', 'svg-ao-track-marker-eo');

		return {
			remove() {
				segments.forEach(s => s.remove());
			}
		};
	}
}